import * as React from 'react';

import { List } from '@mui/material';
import { useLocation } from 'react-router-dom';

import {
  type SidebarLinkConfig,
  getSidebarLinks,
} from '@app/components/sidebar/sidebarLinkConfig';
import { SidebarNavGroup } from '@app/components/sidebar/SidebarNavGroup';
import { SidebarNavItem } from '@app/components/sidebar/SidebarNavItem';
import { isPathActive } from '@app/components/sidebar/sidebarNavStyles';
import { useSidebarState } from '@app/components/sidebar/SidebarStateProvider';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { useLocale } from '@shared/hooks/locale';

const findGroupKeyForPathname = (
  links: SidebarLinkConfig[],
  pathname: string,
): string | null =>
  links.find(
    link =>
      link.type === 'groupHeader' &&
      (isPathActive(link.path, pathname) ||
        link.children.some(child => isPathActive(child.path, pathname))),
  )?.key ?? null;

export const SidebarNavList = ({ collapsed }: { collapsed: boolean }) => {
  const location = useLocation();
  const { t, countryCode } = useLocale();
  const { me, hasuraRole } = useAppData();
  const { isMobile, closeMobile } = useSidebarState();

  const links = React.useMemo(
    () =>
      getSidebarLinks(me, t, countryCode, hasuraRole).filter(
        link => link.visible,
      ),
    [me, t, countryCode, hasuraRole],
  );

  // Single-open accordion; the group owning the current route opens
  // automatically on load and stays in sync while navigating.
  const [activeGroupKey, setActiveGroupKey] = React.useState<string | null>(
    () => findGroupKeyForPathname(links, location.pathname),
  );

  React.useEffect(() => {
    const key = findGroupKeyForPathname(links, location.pathname);
    if (key != null) {
      setActiveGroupKey(key);
    }
  }, [links, location.pathname]);

  return (
    <List
      sx={{ flexGrow: 1, pt: 0.5, pb: '50px' }}
      onClick={() => {
        // navigation clicks bubble here; group toggles stop propagation
        if (isMobile) {
          closeMobile();
        }
      }}
    >
      {links.map(link =>
        link.type === 'link' ? (
          <SidebarNavItem key={link.key} link={link} collapsed={collapsed} />
        ) : (
          <SidebarNavGroup
            key={link.key}
            link={link}
            collapsed={collapsed}
            open={!collapsed && activeGroupKey === link.key}
            onToggle={() =>
              setActiveGroupKey(key => (key === link.key ? null : link.key))
            }
          />
        ),
      )}
    </List>
  );
};
